import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Button, Textarea } from '@cargomatic/ui';
import { Badge } from '../molecules/badge-fix';
import { FilterTextInput } from '../molecules/FilterTextInput';
/**
 * Shared text-list filter for entering reference IDs (shipment IDs, order IDs, etc.).
 * Supports comma-separated and newline-separated input with deduplication.
 * Wrap with FilterGroup at the page level (standalone) or pass `inline` when
 * placed inside a FilterChip popover.
 */
export function ReferenceFilter({ value, onChange, placeholder = 'Enter reference...', badgeSuffix = 'refs', inline = false, }) {
    const { t } = useTranslation();
    const handleAddRaw = (raw) => {
        const items = raw
            .replace(/\n/g, ',')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const unique = [...new Set([...value, ...items])];
        onChange(unique);
    };
    if (inline) {
        return (React.createElement(InlineReferenceEditor, { placeholder: placeholder, addLabel: titleize(t('ADD')), values: value, onAdd: handleAddRaw, onRemove: (item) => onChange(value.filter((v) => v !== item)) }));
    }
    return (React.createElement(FilterTextInput, { placeholder: placeholder, values: value, addLabel: titleize(t('ADD')), badgeSuffix: badgeSuffix, onAdd: handleAddRaw, onRemove: (item) => onChange(value.filter((v) => v !== item)), onClear: () => onChange([]) }));
}
function InlineReferenceEditor({ placeholder, addLabel, values, onAdd, onRemove, }) {
    const [input, setInput] = useState('');
    const handleAdd = () => {
        if (input.trim()) {
            onAdd(input.trim());
            setInput('');
        }
    };
    return (React.createElement("div", { className: "space-y-2" },
        React.createElement("div", { className: "flex gap-1.5" },
            React.createElement(Textarea, { placeholder: placeholder, value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAdd();
                    }
                }, rows: 3, className: "text-sm resize-none", autoFocus: true }),
            React.createElement(Button, { size: "sm", className: "h-8 shrink-0", onClick: handleAdd }, addLabel)),
        values.length > 0 && (React.createElement("div", { className: "flex flex-wrap gap-1" }, values.map((val) => (React.createElement(Badge, { key: val, variant: "secondary", className: "gap-0.5 pr-0.5 text-xs" },
            val,
            React.createElement("button", { type: "button", className: "inline-flex items-center justify-center size-3 shrink-0 cursor-pointer", "aria-label": `Remove ${val}`, onClick: () => onRemove(val) },
                React.createElement(X, { className: "size-3" })))))))));
}
